import React, { useEffect, useRef, useState } from 'react';

interface SectionProps {
  id: string;
  title: string;
  className?: string;
  children: React.ReactNode;
  backgroundImage?: string;
  textColor?: string;
  overlayStyle?: 'normal' | 'darker';
  applyBlur?: boolean;
}

const Section: React.FC<SectionProps> = ({
  id,
  title,
  className = "",
  children,
  backgroundImage,
  textColor = "text-wedding-burgundy",
  overlayStyle = "normal",
  applyBlur
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [bgImageLoaded, setBgImageLoaded] = useState(false);

  useEffect(() => {
    if (backgroundImage) {
      const img = new Image();
      img.src = backgroundImage;
      img.onload = () => setBgImageLoaded(true);
      img.onerror = () => {
        console.error(`Failed to load background image: ${backgroundImage}`);
        setBgImageLoaded(false);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      const elements = currentRef.querySelectorAll('.reveal');
      elements.forEach(el => observer.observe(el));
    }

    return () => {
      if (currentRef) {
        const elements = currentRef.querySelectorAll('.reveal');
        elements.forEach(el => observer.unobserve(el));
      }
    };
  }, [backgroundImage]);

  return (
    <section
      id={id}
      ref={sectionRef}
      className={`relative min-h-screen ${className}`}
    >
      {backgroundImage && bgImageLoaded && (
        <>
          <div 
            className="absolute inset-0 z-0"
            style={{ 
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: applyBlur ? 'blur(0.5px)' : 'none',
              transition: 'filter 0.5s ease-in-out',
            }}
          />
          <div 
            className={`absolute inset-0 z-10 ${overlayStyle === 'darker' ? 'bg-black/60' : 'bg-black/40'}`}
          />
        </>
      )}
      
      <div className="relative z-30 min-h-screen flex flex-col justify-center py-16">
        <div className="container mx-auto px-4">
          {title && (
            <h2 className={`text-3xl md:text-4xl font-playfair font-semibold mb-12 text-center reveal
              ${backgroundImage && bgImageLoaded ? 'text-white' : textColor}`}
            >
              {title}
            </h2>
          )}
          {children}
        </div>
      </div>
    </section>
  );
};

export default Section;
